import styled, { keyframes, css } from 'styled-components';
import { theme } from './theme';

// ===== ANIMATIONS =====

export const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(24px); }
  to { opacity: 1; transform: translateY(0); }
`;

export const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

export const slideDown = keyframes`
  from { opacity: 0; transform: translateY(-8px); }
  to { opacity: 1; transform: translateY(0); }
`;

// ===== LAYOUT =====

export const PageContainer = styled.div`
  min-height: 100vh;
  width: 100%;
  max-width: 100vw;
  background: ${theme.colors.bgPrimary};
  color: ${theme.colors.textPrimary};
  font-family: ${theme.fonts.sans};
  -webkit-font-smoothing: antialiased;
  text-align: left;
  /* overflow-x: clip removido — quebrava position:sticky nos descendentes */
`;

export const Container = styled.div`
  max-width: 1200px;
  width: 100%;
  margin: 0 auto;
  padding: 0 ${theme.spacing.md};
  box-sizing: border-box;

  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: 0 ${theme.spacing.sm};
  }
`;

export const Section = styled.section`
  padding: ${theme.spacing.section} 0;

  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: ${theme.spacing.xxl} 0;
  }
`;

export const SectionDark = styled(Section)`
  background: ${theme.colors.bgDark};
  color: #FFFFFF;
`;

export const SectionLight = styled(Section)`
  background: ${theme.colors.bgSection};
`;

// ===== TYPOGRAPHY =====

export const PreTitle = styled.span`
  display: block;
  font-size: 12px;
  font-weight: 500;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: ${theme.colors.accentWarm};
  margin-bottom: ${theme.spacing.sm};
`;

export const HeadingSerif = styled.h2`
  font-family: ${theme.fonts.serif};
  font-weight: 300;
  letter-spacing: -0.03em;
  line-height: 1.2;
  color: inherit;
  margin: 0 0 ${theme.spacing.md} 0;

  ${({ $size }) => {
    switch ($size) {
      case 'xl': return css`font-size: 56px; @media (max-width: ${theme.breakpoints.mobile}) { font-size: 36px; }`;
      case 'lg': return css`font-size: 40px; @media (max-width: ${theme.breakpoints.mobile}) { font-size: 28px; }`;
      case 'md': return css`font-size: 32px; @media (max-width: ${theme.breakpoints.mobile}) { font-size: 24px; }`;
      default: return css`font-size: 40px; @media (max-width: ${theme.breakpoints.mobile}) { font-size: 28px; }`;
    }
  }}
`;

export const BodyText = styled.p`
  font-size: 16px;
  line-height: 1.6;
  color: ${({ $muted }) => $muted ? theme.colors.textSecondary : 'inherit'};
  margin: 0 0 ${theme.spacing.sm} 0;
`;

// ===== BUTTONS =====

export const ButtonSolid = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 14px 32px;
  background: ${theme.colors.bgDark};
  color: #FFFFFF;
  border: none;
  border-radius: 4px;
  font-family: ${theme.fonts.sans};
  font-size: 14px;
  font-weight: 500;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  cursor: pointer;
  transition: all ${theme.transitions.base};
  text-decoration: none;

  &:hover {
    background: #1A3050;
    transform: translateY(-1px);
  }

  ${({ $light }) => $light && css`
    background: ${theme.colors.bgCard};
    color: ${theme.colors.bgDark};
    &:hover { background: ${theme.colors.bgSection}; }
  `}

  ${({ $fullWidth }) => $fullWidth && css`width: 100%;`}
`;

export const ButtonOutline = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 14px 32px;
  background: transparent;
  color: ${({ $light }) => $light ? '#FFFFFF' : theme.colors.textPrimary};
  border: 1px solid ${({ $light }) => $light ? 'rgba(255,255,255,0.4)' : theme.colors.textPrimary};
  border-radius: 4px;
  font-family: ${theme.fonts.sans};
  font-size: 14px;
  font-weight: 500;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  cursor: pointer;
  transition: all ${theme.transitions.base};
  text-decoration: none;

  &:hover {
    background: ${({ $light }) => $light ? 'rgba(255,255,255,0.1)' : theme.colors.textPrimary};
    color: ${({ $light }) => $light ? '#FFFFFF' : '#FFFFFF'};
  }

  ${({ $fullWidth }) => $fullWidth && css`width: 100%;`}
`;

// ===== NAVBAR =====

export const Nav = styled.nav`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 100;
  height: 72px;
  display: flex;
  align-items: center;
  padding: 0 ${theme.spacing.lg};
  background: ${({ $scrolled }) => $scrolled ? theme.colors.bgCard : 'transparent'};
  border-bottom: ${({ $scrolled }) => $scrolled ? `1px solid ${theme.colors.border}` : 'none'};
  box-shadow: ${({ $scrolled }) => $scrolled ? theme.shadows.sticky : 'none'};
  transition: background ${theme.transitions.base}, border-color ${theme.transitions.base}, box-shadow ${theme.transitions.base};

  @media (max-width: ${theme.breakpoints.mobile}) {
    height: 56px;
    padding: 0 ${theme.spacing.sm};
  }
`;

export const NavInner = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`;

export const NavLogo = styled.a`
  display: inline-flex;
  align-items: center;
  font-family: ${theme.fonts.serif};
  font-size: 22px;
  font-weight: 400;
  letter-spacing: -0.02em;
  color: ${({ $scrolled }) => $scrolled ? theme.colors.textPrimary : '#FFFFFF'};
  text-decoration: none;
  transition: opacity ${theme.transitions.base}, color ${theme.transitions.base};
  opacity: ${({ $hidden }) => ($hidden ? 0 : 1)};
  pointer-events: ${({ $hidden }) => ($hidden ? 'none' : 'auto')};

  .mf-navbar-logo {
    height: 32px;
    width: auto;
    object-fit: contain;
    display: block;
  }

  @media (max-width: ${theme.breakpoints.mobile}) {
    .mf-navbar-logo {
      height: 22px;
    }
  }
`;

export const NavLinks = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.lg};

  @media (max-width: ${theme.breakpoints.mobile}) {
    display: none;
  }
`;

export const NavLink = styled.a`
  font-size: 13px;
  font-weight: 500;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: ${({ $scrolled }) => $scrolled ? theme.colors.textPrimary : '#FFFFFF'};
  text-decoration: none;
  position: relative;
  transition: color ${theme.transitions.base};

  &::after {
    content: '';
    position: absolute;
    bottom: -4px;
    left: 0;
    width: 0;
    height: 1px;
    background: currentColor;
    transition: width ${theme.transitions.base};
  }

  &:hover::after {
    width: 100%;
  }
`;

export const NavMenuButton = styled.button`
  display: none;
  background: none;
  border: none;
  color: ${({ $scrolled }) => $scrolled ? theme.colors.textPrimary : '#FFFFFF'};
  font-size: 24px;
  cursor: pointer;
  padding: 8px;

  @media (max-width: ${theme.breakpoints.mobile}) {
    display: block;
  }
`;

export const NavThemeToggle = styled.button`
  background: ${({ $scrolled }) => $scrolled ? theme.colors.bgCard : 'rgba(255,255,255,0.14)'};
  border: 1px solid ${({ $scrolled }) => $scrolled ? theme.colors.border : 'rgba(255,255,255,0.25)'};
  color: ${({ $scrolled }) => $scrolled ? theme.colors.textPrimary : '#FFFFFF'};
  width: 38px;
  height: 38px;
  border-radius: 50%;
  cursor: pointer;
  font-size: 15px;
  line-height: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: transform .25s, background .2s, color .2s;
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  &:hover { transform: rotate(20deg); }
  margin-left: ${theme.spacing.sm};

  @media (max-width: ${theme.breakpoints.mobile}) {
    width: 34px;
    height: 34px;
    font-size: 14px;
    margin-left: 4px;
  }
`;

export const MobileMenuOverlay = styled.div`
  display: none;

  @media (max-width: ${theme.breakpoints.mobile}) {
    display: flex;
    position: fixed;
    inset: 0;
    z-index: 99;
    background: rgba(11, 25, 41, 0.96);
    backdrop-filter: blur(8px);
    -webkit-backdrop-filter: blur(8px);
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: ${theme.spacing.lg};
    padding: 72px ${theme.spacing.lg} ${theme.spacing.xl};
    animation: mfMenuFadeIn 200ms ease-out;
  }

  @keyframes mfMenuFadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
`;

export const MobileMenuLink = styled.a`
  font-family: ${theme.fonts.sans};
  font-size: 20px;
  font-weight: 500;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #FFFFFF;
  text-decoration: none;
  padding: 12px 16px;
  cursor: pointer;

  &:active {
    opacity: 0.6;
  }
`;

// ===== HERO =====

export const HeroWrapper = styled.section`
  position: relative;
  height: 100vh;
  min-height: 600px;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  overflow: hidden;
  background: #0B1929;
`;

export const HeroBg = styled.div`
  position: absolute;
  inset: 0;
  background: url(${({ $src }) => $src}) center/cover no-repeat;
  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: ${theme.colors.overlay};
  }
`;

export const HeroBgVideo = styled.div`
  position: absolute;
  inset: 0;
  overflow: hidden;
  background: ${({ $poster }) => ($poster ? `url(${$poster}) center/cover no-repeat` : '#0B1929')};
  video {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: ${theme.colors.overlay};
  }
`;

export const HeroContent = styled.div`
  position: relative;
  z-index: 2;
  max-width: 720px;
  padding: 0 ${theme.spacing.md};
  animation: ${fadeInUp} 0.8s ease-out;
`;

export const HeroTitle = styled.h1`
  font-family: ${theme.fonts.serif};
  font-size: 56px;
  font-weight: 300;
  letter-spacing: -0.03em;
  line-height: 1.15;
  color: #FFFFFF;
  margin: 0 0 ${theme.spacing.md} 0;

  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 36px;
  }
`;

export const HeroSubtitle = styled.p`
  font-size: 18px;
  font-weight: 300;
  line-height: 1.5;
  color: rgba(255,255,255,0.85);
  margin: 0 0 ${theme.spacing.lg} 0;

  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 16px;
  }
`;

export const HeroSearchBar = styled.form`
  display: flex;
  align-items: stretch;
  gap: 0;
  background: ${theme.colors.bgCard};
  backdrop-filter: blur(14px);
  -webkit-backdrop-filter: blur(14px);
  border-radius: 6px;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.22);
  overflow: visible;
  max-width: 1240px;
  width: 100%;
  margin: 0 auto;

  @media (max-width: ${theme.breakpoints.tablet}) {
    max-width: 100%;
  }

  @media (max-width: ${theme.breakpoints.mobile}) {
    flex-direction: column;
    max-width: 100%;
  }
`;

export const HeroSearchField = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  position: relative;
  background: transparent;
  border-right: 1px solid var(--mf-hairline);
  padding: 0 18px;
  min-width: 0;
  transition: background ${theme.transitions.fast};

  &:last-of-type {
    border-right: none;
  }

  &:hover {
    background: var(--mf-field-hover);
  }

  &:focus-within {
    background: var(--mf-field-focus);
  }

  select {
    appearance: none;
    -webkit-appearance: none;
    -moz-appearance: none;
    flex: 1;
    border: none;
    outline: none;
    background: transparent;
    font-family: ${theme.fonts.sans};
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: ${({ $placeholder }) => ($placeholder ? theme.colors.textMuted : theme.colors.textPrimary)};
    padding: 18px 24px 18px 0;
    min-width: 0;
    width: 100%;
    cursor: pointer;
    background-image: url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'%3E%3Cpath fill='none' stroke='currentColor' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round' d='M1 1l4 4 4-4'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right center;
    background-size: 10px 6px;
  }

  select option {
    text-transform: none;
    font-size: 14px;
    font-weight: 400;
    letter-spacing: 0;
    color: ${theme.colors.textPrimary};
    background: ${theme.colors.bgCard};
  }

  @media (max-width: ${theme.breakpoints.mobile}) {
    border-right: none;
    border-bottom: 1px solid var(--mf-hairline);
    padding: 0 20px;

    select {
      padding: 16px 28px 16px 0;
    }
  }
`;

export const HeroSearchSubmit = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${theme.colors.bgDark};
  color: #FFFFFF;
  border: none;
  padding: 0 22px;
  min-width: 60px;
  font-size: 18px;
  cursor: pointer;
  transition: background ${theme.transitions.fast};

  &:hover {
    background: #152c47;
  }

  &:focus-visible {
    outline: 2px solid ${theme.colors.accent};
    outline-offset: -4px;
  }

  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: 16px;
    gap: 10px;
    font-size: 15px;
    font-weight: 600;
    letter-spacing: 0.1em;
    text-transform: uppercase;

    &::after {
      content: 'Pesquisar';
    }
  }
`;

export const ScrollIndicator = styled.div`
  position: absolute;
  bottom: ${theme.spacing.lg};
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  color: rgba(255,255,255,0.6);
  font-size: 11px;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  animation: ${fadeIn} 1.5s ease-out 0.5s both;

  &::after {
    content: '';
    width: 1px;
    height: 40px;
    background: rgba(255,255,255,0.3);
    animation: ${keyframes`
      0%, 100% { transform: scaleY(0); transform-origin: top; }
      50% { transform: scaleY(1); transform-origin: top; }
    `} 2s ease-in-out infinite;
  }
`;

// ===== PROPERTY CARD (Compass-inspired) =====

export const PropertyGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: ${theme.spacing.gutter};
`;

export const CarouselSection = styled.div`
  position: relative;
  margin-top: ${theme.spacing.xl};
`;

export const CarouselTrack = styled.div`
  display: flex;
  gap: ${theme.spacing.gutter};
  overflow-x: auto;
  scroll-snap-type: x mandatory;
  scroll-behavior: smooth;
  padding-bottom: ${theme.spacing.sm};

  &::-webkit-scrollbar {
    height: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background: ${theme.colors.border};
    border-radius: 2px;
  }

  & > * {
    flex: 0 0 300px;
    scroll-snap-align: start;

    @media (max-width: ${theme.breakpoints.mobile}) {
      flex: 0 0 260px;
    }
  }
`;

export const CarouselBtn = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  ${({ $dir }) => $dir === 'left' ? 'left: -24px;' : 'right: -24px;'}
  width: 52px;
  height: 52px;
  border-radius: 50%;
  border: 1px solid ${theme.colors.border};
  background: ${theme.colors.bgCard};
  box-shadow: ${theme.shadows.md};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 24px;
  color: ${theme.colors.textPrimary};
  transition: all ${theme.transitions.fast};
  z-index: 2;

  &:hover {
    box-shadow: ${theme.shadows.lg};
    background: ${theme.colors.bgSection};
  }

  @media (max-width: ${theme.breakpoints.mobile}) {
    display: none;
  }
`;

export const Card = styled.div`
  border-radius: 8px;
  overflow: hidden;
  background: ${theme.colors.bgCard};
  box-shadow: ${theme.shadows.sm};
  transition: all ${theme.transitions.base};
  cursor: pointer;
  text-decoration: none;
  color: inherit;

  &:hover {
    box-shadow: ${theme.shadows.md};
    transform: translateY(-2px);
  }
`;

export const CardImage = styled.div`
  position: relative;
  width: 100%;
  padding-bottom: 66.67%; /* 3:2 ratio */
  overflow: hidden;

  img, video {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform ${theme.transitions.slow};
  }

  ${Card}:hover & img,
  ${Card}:hover & video {
    transform: scale(1.03);
  }
`;

export const CardBadge = styled.span`
  position: absolute;
  bottom: 8px;
  left: 8px;
  background: rgba(0,0,0,0.75);
  color: #FFFFFF;
  padding: 4px 10px;
  border-radius: 3px;
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  z-index: 2;
`;

export const CardBody = styled.div`
  padding: ${theme.spacing.sm} ${theme.spacing.sm} ${theme.spacing.md};
`;

export const CardPrice = styled.div`
  font-size: 22px;
  font-weight: 600;
  color: ${theme.colors.textPrimary};
  margin-bottom: 6px;
`;

export const CardAddress = styled.div`
  font-size: 15px;
  font-weight: 400;
  color: ${theme.colors.textPrimary};
  margin-bottom: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const CardNeighborhood = styled.div`
  font-size: 14px;
  color: ${theme.colors.textSecondary};
  margin-bottom: 10px;
`;

export const CardTitle = styled.div`
  font-size: 15px;
  font-weight: 500;
  color: ${theme.colors.textPrimary};
  margin-bottom: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const CardSubtitle = styled.div`
  font-size: 13px;
  color: ${theme.colors.textSecondary};
  margin-bottom: 12px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const CardSpecs = styled.div`
  display: flex;
  align-items: center;
  gap: 0;
  font-size: 13px;
  color: ${theme.colors.textSecondary};

  span {
    display: flex;
    align-items: center;
    gap: 5px;
    padding: 0 10px;
    border-right: 1px solid ${theme.colors.border};

    &:first-child { padding-left: 0; }
    &:last-child { border-right: none; }
  }

  svg {
    width: 15px;
    height: 15px;
    flex-shrink: 0;
    fill: none;
    stroke: ${theme.colors.accentWarm};
    stroke-width: 1.8;
    stroke-linecap: round;
    stroke-linejoin: round;
  }

  strong {
    font-weight: 500;
    color: ${theme.colors.textPrimary};
  }

  small {
    color: ${theme.colors.textMuted};
    font-size: 11px;
  }
`;

// ===== CORRETOR CTA SECTION =====

const corretorFadeUp = keyframes`
  from { opacity: 0; transform: translateY(24px); }
  to { opacity: 1; transform: translateY(0); }
`;

export const CorretorHeroCta = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: center;
  gap: 40px;
  padding: 48px 40px;
  background: linear-gradient(135deg, rgba(184,147,90,.08), rgba(184,147,90,.03));
  border: 1px solid rgba(184,147,90,.18);
  border-radius: 16px;
  margin-bottom: 80px;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: -50%;
    right: -20%;
    width: 400px;
    height: 400px;
    background: radial-gradient(circle, rgba(184,147,90,.06) 0%, transparent 70%);
    pointer-events: none;
  }

  @media (max-width: ${theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
    padding: 32px 24px;
    text-align: center;
  }
`;

export const CorretorPre = styled.div`
  font-size: 11px;
  letter-spacing: 2.5px;
  text-transform: uppercase;
  color: #C9A06A;
  font-weight: 600;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  gap: 8px;

  @media (max-width: ${theme.breakpoints.mobile}) {
    justify-content: center;
  }
`;

export const CorretorCtaBtn = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 16px 32px;
  background: #B8935A;
  color: #fff;
  border: none;
  border-radius: 8px;
  font-family: ${theme.fonts.sans};
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0.5px;
  text-decoration: none;
  cursor: pointer;
  white-space: nowrap;
  transition: transform .2s, box-shadow .2s;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 12px 32px rgba(184,147,90,.3);
  }

  @media (max-width: ${theme.breakpoints.mobile}) {
    margin: 0 auto;
  }
`;

export const CorretorAreaBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 20px;
  background: rgba(184,147,90,.1);
  border: 1px solid rgba(184,147,90,.25);
  border-radius: 999px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 2px;
  text-transform: uppercase;
  color: #C9A06A;
  margin-bottom: 20px;
`;

export const CorretorSteps = styled.div`
  display: grid;
  grid-template-columns: 1fr 120px 1fr 120px 1fr;
  align-items: start;

  @media (max-width: ${theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
    gap: 40px;
  }
`;

export const CorretorStep = styled.div`
  text-align: center;
  padding: 0 16px;
  opacity: 0;
  transform: translateY(24px);
  animation: ${corretorFadeUp} 0.6s ease forwards;
  animation-delay: ${({ $delay }) => $delay || '0s'};

  .dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: rgba(184,147,90,.35);
    margin: 12px auto 20px;
  }

  h4 {
    font-size: 16px;
    font-weight: 600;
    margin-bottom: 8px;
    color: #F5F1E8;
  }

  p {
    font-size: 13px;
    color: rgba(245,241,232,.5);
    line-height: 1.55;
    max-width: 240px;
    margin: 0 auto 16px;
  }
`;

const arcDraw = keyframes`
  from { stroke-dashoffset: 220; }
  to { stroke-dashoffset: 0; }
`;

export const CorretorStepIcon = styled.div`
  position: relative;
  width: 96px;
  height: 96px;
  margin: 0 auto 8px;

  .circle {
    position: absolute;
    inset: 8px;
    border-radius: 50%;
    background: rgba(184,147,90,.08);
    border: 1.5px solid rgba(184,147,90,.15);
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background .3s;
  }

  ${CorretorStep}:hover & .circle {
    background: rgba(184,147,90,.12);
  }

  .circle svg {
    width: 32px;
    height: 32px;
    stroke: #C9A06A;
    stroke-width: 1.5;
    fill: none;
    stroke-linecap: round;
    stroke-linejoin: round;
  }

  .arc {
    position: absolute;
    inset: 0;
    width: 96px;
    height: 96px;
  }

  .arc circle {
    fill: none;
    stroke: #C9A06A;
    stroke-width: 3;
    stroke-linecap: round;
    stroke-dasharray: 220;
    stroke-dashoffset: 220;
    transform-origin: center;
    animation: ${arcDraw} 1.2s ease forwards;
  }

  &[data-step="1"] .arc circle {
    stroke-dasharray: 190 80;
    stroke-dashoffset: 0;
    transform: rotate(-90deg);
    animation-delay: 0.2s;
  }
  &[data-step="2"] .arc circle {
    stroke-dasharray: 190 80;
    stroke-dashoffset: 0;
    transform: rotate(-90deg);
    animation-delay: 0.5s;
  }
  &[data-step="3"] .arc circle {
    stroke-dasharray: 220 50;
    stroke-dashoffset: 0;
    transform: rotate(-90deg);
    animation-delay: 0.8s;
  }

  .num {
    position: absolute;
    top: -2px;
    right: -2px;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background: #C9A06A;
    color: #0F1117;
    font-size: 11px;
    font-weight: 700;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 2;
  }

  .check {
    position: absolute;
    bottom: -2px;
    right: -2px;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background: rgba(184,147,90,.15);
    border: 1.5px solid rgba(184,147,90,.4);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 2;

    svg {
      width: 12px;
      height: 12px;
      stroke: #C9A06A;
      stroke-width: 2.5;
      fill: none;
    }
  }
`;

export const CorretorStepBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 16px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  text-decoration: none;
  transition: transform .2s, background .2s;

  &:hover { transform: translateY(-1px); }

  background: ${({ $variant }) => $variant === 'tag'
    ? 'rgba(184,147,90,.1)'
    : 'rgba(245,241,232,.08)'};
  border: 1px solid ${({ $variant }) => $variant === 'tag'
    ? 'rgba(184,147,90,.3)'
    : 'rgba(245,241,232,.2)'};
  color: ${({ $variant }) => $variant === 'tag' ? '#C9A06A' : '#F5F1E8'};

  &:hover {
    background: ${({ $variant }) => $variant === 'tag'
      ? 'rgba(184,147,90,.18)'
      : 'rgba(245,241,232,.14)'};
  }

  svg {
    width: 13px;
    height: 13px;
    stroke: ${({ $variant }) => $variant === 'tag' ? '#C9A06A' : '#F5F1E8'};
    stroke-width: 2;
    stroke-linecap: round;
    fill: none;
  }
`;

const dashMarch = keyframes`
  to { stroke-dashoffset: -28; }
`;

export const CorretorConnector = styled.div`
  display: flex;
  align-items: center;
  padding-top: 36px;

  svg {
    width: 100%;
    height: 4px;
    overflow: visible;

    line {
      stroke: rgba(184,147,90,.3);
      stroke-width: 2;
      stroke-dasharray: 8 6;
      stroke-linecap: round;
      animation: ${dashMarch} 1.5s linear infinite;
    }
  }

  @media (max-width: ${theme.breakpoints.mobile}) {
    display: none;
  }
`;

// ===== PROPERTY DETAIL =====

export const DetailGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 360px;
  gap: ${theme.spacing.lg};
  align-items: start;
  padding-top: ${theme.spacing.lg};

  @media (max-width: ${theme.breakpoints.tablet}) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

export const GalleryMain = styled.div`
  position: relative;
  width: 100%;
  height: 420px;
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  @media (max-width: ${theme.breakpoints.tablet}) {
    height: 320px;
  }

  @media (max-width: ${theme.breakpoints.mobile}) {
    height: 240px;
  }
`;

export const PhotoCount = styled.span`
  position: absolute;
  bottom: 12px;
  right: 12px;
  background: rgba(0,0,0,0.6);
  color: #FFFFFF;
  padding: 4px 12px;
  border-radius: 4px;
  font-size: 13px;
  font-weight: 500;
`;

export const Filmstrip = styled.div`
  display: flex;
  gap: 8px;
  overflow-x: auto;
  padding: 10px 0;
  scroll-snap-type: x mandatory;

  &::-webkit-scrollbar {
    height: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background: ${theme.colors.border};
    border-radius: 2px;
  }
`;

export const FilmstripThumb = styled.button`
  flex: 0 0 100px;
  height: 66px;
  border-radius: 4px;
  overflow: hidden;
  border: 2px solid ${({ $active }) => $active ? theme.colors.accentWarm : 'transparent'};
  cursor: pointer;
  scroll-snap-align: start;
  padding: 0;
  background: none;
  transition: border-color ${theme.transitions.fast};

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

export const SpecsBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${theme.spacing.sm} ${theme.spacing.md};
  padding: ${theme.spacing.sm} 0;
  border-top: 1px solid ${theme.colors.border};
  border-bottom: 1px solid ${theme.colors.border};
  margin: ${theme.spacing.md} 0;
`;

export const SpecItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: ${theme.colors.textPrimary};

  svg {
    width: 18px;
    height: 18px;
    color: ${theme.colors.textSecondary};
  }
`;

export const CTASidebar = styled.div`
  position: sticky;
  top: 88px;
  align-self: start;
  padding: ${theme.spacing.md};
  border: 1px solid ${theme.colors.border};
  border-radius: 12px;
  background: ${theme.colors.bgCard};
  box-shadow: ${theme.shadows.sm};
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.sm};

  @media (max-width: ${theme.breakpoints.tablet}) {
    display: none;
  }
`;

export const CTAMobileBar = styled.div`
  display: none;

  @media (max-width: ${theme.breakpoints.tablet}) {
    display: flex;
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    padding: 12px ${theme.spacing.sm};
    background: ${theme.colors.bgCard};
    border-top: 1px solid ${theme.colors.border};
    box-shadow: ${theme.shadows.sticky};
    z-index: 100;
    gap: 10px;

    ${ButtonSolid}, ${ButtonOutline} {
      flex: 1;
      padding: 12px;
    }
  }
`;

export const PriceDisplay = styled.div`
  .price-main {
    font-size: 32px;
    font-weight: 700;
    color: ${theme.colors.textPrimary};
    line-height: 1.2;
  }
  .price-meter {
    font-size: 14px;
    color: ${theme.colors.textSecondary};
    margin-top: 4px;
  }
`;

// ===== MAP =====

export const MapWrapper = styled.div`
  margin-top: 32px;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid ${theme.colors.border};
  height: 360px;

  iframe {
    display: block;
    width: 100%;
    height: 100%;
    border: 0;
  }

  @media (max-width: ${theme.breakpoints.mobile}) {
    height: 280px;
  }
`;

// ===== DETAIL HERO (Quintoandar-inspired) =====

export const DetailHero = styled.section`
  position: relative;
  padding-top: ${theme.spacing.lg};

  @media (max-width: ${theme.breakpoints.tablet}) {
    padding-top: 0;
  }
`;

export const DetailHeroInfo = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: ${theme.spacing.md} 0;

  @media (max-width: ${theme.breakpoints.tablet}) {
    display: none;
  }
`;

export const DetailHeroTitle = styled.h1`
  font-family: ${theme.fonts.serif};
  font-size: 40px;
  font-weight: 400;
  line-height: 1.15;
  letter-spacing: -0.01em;
  color: ${theme.colors.textPrimary};
  margin: 0 0 ${theme.spacing.lg};

  @media (max-width: ${theme.breakpoints.desktop}) {
    font-size: 34px;
  }
`;

export const DetailHeroPrice = styled.div`
  .label {
    font-family: ${theme.fonts.sans};
    font-size: 13px;
    font-weight: 500;
    color: ${theme.colors.textSecondary};
    letter-spacing: 0.02em;
    margin-bottom: 2px;
  }
  .main {
    font-family: ${theme.fonts.sans};
    font-size: 32px;
    font-weight: 600;
    color: ${theme.colors.textPrimary};
    line-height: 1.1;
    letter-spacing: -0.01em;
  }
  .sub {
    font-family: ${theme.fonts.sans};
    font-size: 14px;
    color: ${theme.colors.textSecondary};
    margin-top: 6px;
  }
  margin-bottom: ${theme.spacing.lg};
`;

export const DetailHeroCtas = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

// Wrapper da galeria — agora full-width (sem split lateral)
export const GalleryBlock = styled.div`
  position: relative;

  @media (max-width: ${theme.breakpoints.tablet}) {
    display: none;
  }
`;

// Galeria mosaico 5 fotos: 1 big à esquerda + 4 small em grid 2x2 à direita
export const GalleryMosaic = styled.div`
  position: relative;
  display: grid;
  grid-template-columns: 2fr 1fr 1fr;
  grid-template-rows: 1fr 1fr;
  gap: 6px;
  height: 540px;
  border-radius: 10px;
  overflow: hidden;

  @media (max-width: ${theme.breakpoints.desktop}) {
    height: 460px;
  }

  @media (max-width: ${theme.breakpoints.tablet}) {
    display: none;
  }
`;

// Botão share (círculo branco) no topo esquerdo da primeira foto.
// top: 32px pra respirar do header fixo sem entrar no meio da foto.
export const GalleryShare = styled.button`
  position: absolute;
  top: 32px;
  left: 16px;
  z-index: 2;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: ${theme.colors.bgCard};
  backdrop-filter: blur(6px);
  border: none;
  box-shadow: 0 2px 8px rgba(0,0,0,0.15);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  color: ${theme.colors.textPrimary};
  transition: background ${theme.transitions.fast}, transform ${theme.transitions.fast};

  &:hover { background: ${theme.colors.bgCard}; }
  &:active { transform: scale(0.95); }
`;

// Toast de feedback "Link copiado"
export const ShareToast = styled.div`
  position: absolute;
  top: 16px;
  left: 64px;
  z-index: 3;
  background: ${theme.colors.bgDark};
  color: #fff;
  padding: 8px 14px;
  border-radius: 8px;
  font-family: ${theme.fonts.sans};
  font-size: 13px;
  font-weight: 500;
  box-shadow: 0 4px 14px rgba(0,0,0,0.2);
  animation: shareToastIn 0.2s ease-out;

  @keyframes shareToastIn {
    from { opacity: 0; transform: translateY(-4px); }
    to { opacity: 1; transform: translateY(0); }
  }
`;

export const MosaicBig = styled.button`
  position: relative;
  padding: 0;
  border: none;
  background: ${theme.colors.bgSection};
  cursor: pointer;
  overflow: hidden;

  img, video {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
    transition: transform ${theme.transitions.base};
  }

  &:hover img,
  &:hover video {
    transform: scale(1.02);
  }
`;

export const MosaicSmall = styled.button`
  position: relative;
  padding: 0;
  border: none;
  background: ${theme.colors.bgSection};
  cursor: pointer;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
    transition: transform ${theme.transitions.base};
  }

  &:hover img {
    transform: scale(1.02);
  }
`;

// Tile big (1ª foto) ocupa as 2 linhas do grid mosaico
export const MosaicBigSpan = styled(MosaicBig)`
  grid-row: 1 / 3;
`;

// Tile "+N mais fotos" — overlay escuro sobre a última foto
export const MosaicMore = styled.button`
  position: relative;
  padding: 0;
  border: none;
  cursor: pointer;
  overflow: hidden;
  background: ${theme.colors.bgSection};

  img { width: 100%; height: 100%; object-fit: cover; }
  .overlay {
    position: absolute; inset: 0;
    background: rgba(0,0,0,.55);
    display: flex; align-items: center; justify-content: center;
    color: #fff;
    font-family: ${theme.fonts.sans};
    font-size: 15px;
    letter-spacing: 1px;
    text-transform: uppercase;
    font-weight: 500;
  }
`;

// Strip de thumbnails embaixo da galeria
export const ThumbStrip = styled.div`
  display: flex;
  gap: 6px;
  overflow-x: auto;
  margin-top: 8px;
  padding-bottom: 4px;
  scrollbar-width: thin;

  @media (max-width: ${theme.breakpoints.tablet}) { display: none; }

  &::-webkit-scrollbar { height: 6px; }
  &::-webkit-scrollbar-thumb { background: ${theme.colors.border}; border-radius: 3px; }
`;

export const ThumbStripItem = styled.button`
  flex: 0 0 110px;
  height: 70px;
  padding: 0;
  border: 2px solid ${({ $active }) => $active ? theme.colors.accentWarm : 'transparent'};
  background: ${theme.colors.bgSection};
  border-radius: 6px;
  cursor: pointer;
  overflow: hidden;
  transition: opacity .15s, border-color .15s;

  img { width: 100%; height: 100%; object-fit: cover; display: block; }
  &:hover { opacity: .85; }
`;

// Pills sobrepostos na galeria
export const MosaicPills = styled.div`
  position: absolute;
  bottom: 16px;
  left: 16px;
  display: flex;
  gap: 8px;
  z-index: 2;
`;

export const MosaicPill = styled.button`
  background: ${theme.colors.bgCard};
  backdrop-filter: blur(6px);
  border: none;
  border-radius: 999px;
  padding: 9px 16px;
  font-family: ${theme.fonts.sans};
  font-size: 13px;
  font-weight: 500;
  color: ${theme.colors.textPrimary};
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  transition: background ${theme.transitions.fast};
  box-shadow: 0 2px 8px rgba(0,0,0,0.15);

  &:hover {
    background: ${theme.colors.bgCard};
  }

  svg {
    width: 15px;
    height: 15px;
  }
`;

// Setas de navegacao do carrossel
// Wrapper das setas — ocupa largura do DetailHero + bleed direito,
// fazendo as setas aparecerem ancoradas à borda direita da VIEWPORT (igual Quinto)
export const GalleryNavRow = styled.div`
  position: absolute;
  bottom: 16px;
  right: calc(50% - 50vw);
  width: 120px;
  height: 44px;
  pointer-events: none; /* deixa clicks passarem pra galeria, só filhos capturam */

  @media (max-width: ${theme.breakpoints.tablet}) {
    display: none;
  }
`;

export const MosaicNav = styled.button`
  position: absolute;
  bottom: 0;
  right: ${({ $dir }) => ($dir === 'right' ? '24px' : '88px')};
  z-index: 3;
  pointer-events: auto;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: ${theme.colors.bgCard};
  backdrop-filter: blur(6px);
  border: 1px solid ${theme.colors.border};
  box-shadow: 0 2px 10px rgba(0,0,0,0.12);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 26px;
  color: ${theme.colors.textPrimary};
  transition: background ${theme.transitions.fast}, transform ${theme.transitions.fast};

  &:hover { background: ${theme.colors.bgCard}; }
  &:active { transform: scale(0.95); }
  &:disabled { opacity: 0.35; cursor: default; }
`;

// Mobile hero: foto full width + titulo sobreposto + pills
export const MobileHero = styled.div`
  display: none;

  @media (max-width: ${theme.breakpoints.tablet}) {
    display: block;
    position: relative;
    width: 100%;
    height: 420px;
    overflow: hidden;
  }

  @media (max-width: ${theme.breakpoints.mobile}) {
    height: 360px;
  }
`;

export const MobileHeroImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

export const MobileHeroTrack = styled.div`
  display: flex;
  width: 100%;
  height: 100%;
  overflow-x: auto;
  overflow-y: hidden;
  scroll-snap-type: x mandatory;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;

  &::-webkit-scrollbar { display: none; }
`;

export const MobileHeroSlide = styled.div`
  flex: 0 0 100%;
  width: 100%;
  height: 100%;
  scroll-snap-align: start;
  scroll-snap-stop: always;
  cursor: pointer;

  img, video {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
    pointer-events: none;
  }
`;

export const MobileHeroDots = styled.div`
  position: absolute;
  bottom: 56px;
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;
  gap: 6px;
  z-index: 2;
  pointer-events: none;
`;

export const MobileHeroDot = styled.span`
  width: ${({ $active }) => ($active ? '20px' : '6px')};
  height: 6px;
  border-radius: 3px;
  background: ${({ $active }) => ($active ? '#FFFFFF' : 'rgba(255,255,255,0.5)')};
  transition: width ${theme.transitions.base}, background ${theme.transitions.base};
  box-shadow: 0 1px 3px rgba(0,0,0,0.3);
`;

export const MobileHeroCounter = styled.div`
  position: absolute;
  top: 72px;
  right: 16px;
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 6px 12px;
  background: rgba(0, 0, 0, 0.55);
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
  border-radius: 999px;
  font-family: ${theme.fonts.sans};
  font-size: 12px;
  font-weight: 500;
  letter-spacing: 0.04em;
  color: #FFFFFF;
  z-index: 2;
  pointer-events: none;

  strong { font-weight: 600; }

  @media (max-width: ${theme.breakpoints.mobile}) {
    top: 68px;
  }
`;

export const MobileHeroGradient = styled.div`
  position: absolute;
  inset: 0 0 auto 0;
  height: 55%;
  background: linear-gradient(to bottom, rgba(0,0,0,0.6) 0%, rgba(0,0,0,0) 100%);
  pointer-events: none;
`;

export const MobileHeroTitle = styled.h1`
  position: absolute;
  top: 96px;
  left: 20px;
  right: 20px;
  margin: 0;
  font-family: ${theme.fonts.serif};
  font-size: 26px;
  font-weight: 400;
  line-height: 1.2;
  color: #FFFFFF;
  letter-spacing: -0.01em;
  text-shadow: 0 2px 8px rgba(0,0,0,0.4);
  overflow-wrap: anywhere;
  word-break: normal;
  hyphens: auto;

  @media (max-width: ${theme.breakpoints.mobile}) {
    top: 84px;
    font-size: 22px;
  }
`;

export const MobileHeroPills = styled.div`
  position: absolute;
  bottom: 16px;
  left: 20px;
  right: 20px;
  display: flex;
  gap: 8px;
  z-index: 2;
`;

// Card CTA mobile (abaixo da foto)
export const MobilePriceCard = styled.div`
  display: none;

  @media (max-width: ${theme.breakpoints.tablet}) {
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: ${theme.spacing.lg} ${theme.spacing.md};
    background: ${theme.colors.bgCard};
    text-align: center;
    align-items: stretch;
    box-sizing: border-box;
    width: 100%;
    max-width: 100%;
    overflow: hidden;
  }

  button, a {
    box-sizing: border-box;
    width: 100%;
    max-width: 100%;
    min-width: 0;
  }

  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: 20px 16px;

    button, a {
      padding: 14px 12px !important;
      font-size: 12.5px !important;
      letter-spacing: 0.03em !important;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }
`;

export const MobilePriceCardTitle = styled.h1`
  font-family: ${theme.fonts.serif};
  font-size: 28px;
  font-weight: 400;
  line-height: 1.2;
  color: ${theme.colors.textPrimary};
  margin: 0;
  overflow-wrap: anywhere;
  word-break: normal;
  hyphens: auto;
  max-width: 100%;
  text-align: center;

  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 20px;
  }

  @media (max-width: 480px) {
    font-size: 18px;
  }

  @media (max-width: 380px) {
    font-size: 16px;
  }
`;

export const MobilePriceLine = styled.div`
  .label {
    font-family: ${theme.fonts.sans};
    font-size: 13px;
    font-weight: 500;
    color: ${theme.colors.textSecondary};
    margin-bottom: 2px;
    letter-spacing: 0.02em;
  }
  .value {
    font-family: ${theme.fonts.sans};
    font-size: 26px;
    font-weight: 600;
    color: ${theme.colors.textPrimary};
    line-height: 1.1;
  }
  .sub {
    font-family: ${theme.fonts.sans};
    font-size: 13px;
    color: ${theme.colors.textSecondary};
    margin-top: 4px;
  }
`;

// Lightbox fullscreen
export const LightboxOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 3000;
  background: rgba(0, 0, 0, 0.95);
  display: flex;
  flex-direction: column;
`;

export const LightboxHeader = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: calc(18px + env(safe-area-inset-top)) 24px 18px;
  color: #FFFFFF;
  font-family: ${theme.fonts.sans};
  font-size: 14px;

  button {
    background: none;
    border: 1px solid rgba(255,255,255,0.3);
    color: #FFFFFF;
    border-radius: 999px;
    padding: 8px 18px;
    font-family: ${theme.fonts.sans};
    font-size: 13px;
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;

    &:hover { background: rgba(255,255,255,0.1); }
  }
`;

export const LightboxContent = styled.div`
  flex: 1;
  display: flex;
  min-height: 0;
  gap: 0;

  @media (max-width: ${theme.breakpoints.mobile}) {
    flex-direction: column;
  }
`;

export const LightboxBody = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  padding: 0 60px;
  min-height: 0;

  img, video {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
  }

  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: 0 16px;
  }
`;

export const LightboxThumbs = styled.div`
  flex-shrink: 0;
  width: 90px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 8px 10px;
  overflow-y: auto;
  overflow-x: hidden;

  &::-webkit-scrollbar { width: 3px; }
  &::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.2); border-radius: 4px; }

  @media (max-width: ${theme.breakpoints.mobile}) {
    width: 100%;
    flex-direction: row;
    overflow-x: auto;
    overflow-y: hidden;
    padding: 8px 16px;
  }
`;

export const LightboxThumb = styled.button`
  flex-shrink: 0;
  width: 70px;
  height: 52px;
  border-radius: 4px;
  overflow: hidden;
  border: 2px solid ${({ $active }) => $active ? 'rgba(184,147,90,.9)' : 'transparent'};
  opacity: ${({ $active }) => $active ? 1 : 0.5};
  cursor: pointer;
  padding: 0;
  background: none;
  transition: opacity .15s, border-color .15s;

  &:hover { opacity: 0.85; }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }

  @media (max-width: ${theme.breakpoints.mobile}) {
    width: 56px;
    height: 42px;
  }
`;

export const LightboxNav = styled.button`
  position: absolute;
  top: 50%;
  ${({ $dir }) => ($dir === 'left' ? 'left: 16px;' : 'right: 16px;')};
  transform: translateY(-50%);
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255,255,255,0.25);
  color: #FFFFFF;
  font-size: 24px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(4px);

  &:hover { background: rgba(255,255,255,0.2); }
  &:disabled { opacity: 0.25; cursor: default; }

  @media (max-width: ${theme.breakpoints.mobile}) {
    width: 40px;
    height: 40px;
  }
`;

// Data "publicado ha X"
export const PublishedMeta = styled.div`
  font-family: ${theme.fonts.sans};
  font-size: 12px;
  color: ${theme.colors.textMuted};
  letter-spacing: 0.04em;
  text-transform: uppercase;
  margin-bottom: 10px;
`;

// ===== FILTER BAR =====

export const FilterBarWrapper = styled.div`
  position: sticky;
  top: 72px;
  z-index: 90;
  background: ${theme.colors.bgCard};
  border-bottom: 1px solid ${theme.colors.border};
  padding: 10px 0;

  @media (max-width: ${theme.breakpoints.mobile}) {
    top: 56px;
    padding: 8px 0;
  }
`;

export const FilterRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`;

export const FilterChip = styled.button`
  padding: 8px 16px;
  border: 1px solid ${({ $active }) => $active ? theme.colors.textPrimary : theme.colors.border};
  border-radius: 20px;
  background: ${({ $active }) => $active ? theme.colors.textPrimary : 'transparent'};
  color: ${({ $active }) => $active ? '#FFFFFF' : theme.colors.textPrimary};
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all ${theme.transitions.fast};
  white-space: nowrap;

  &:hover {
    border-color: ${theme.colors.textPrimary};
  }
`;

export const FilterCount = styled.span`
  font-size: 14px;
  color: ${theme.colors.textSecondary};
  margin-left: auto;
  white-space: nowrap;
`;

export const FilterGrid = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  @media (max-width: ${theme.breakpoints.tablet}) {
    flex-wrap: wrap;
    gap: 8px;
  }
`;

export const FilterSelect = styled.select`
  appearance: none;
  background: ${theme.colors.bgCard} url("data:image/svg+xml,%3Csvg width='10' height='6' viewBox='0 0 10 6' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1l4 4 4-4' stroke='%238A8F9A' stroke-width='1.5' fill='none' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E") no-repeat right 8px center;
  padding: 8px 26px 8px 10px;
  border: 1px solid ${theme.colors.border};
  border-radius: 6px;
  font-family: ${theme.fonts.sans};
  font-size: 13px;
  font-weight: 500;
  color: ${({ value }) => value ? theme.colors.textPrimary : theme.colors.textSecondary};
  cursor: pointer;
  transition: border-color ${theme.transitions.fast};
  min-width: 0;

  &:hover, &:focus {
    border-color: ${theme.colors.textPrimary};
    outline: none;
  }

  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 12px;
    padding: 7px 24px 7px 8px;
    flex: 1 1 calc(50% - 4px);
  }
`;

export const FilterToggleGroup = styled.div`
  display: flex;
  border: 1px solid ${theme.colors.border};
  border-radius: 6px;
  overflow: hidden;
`;

export const FilterToggle = styled.button`
  padding: 8px 14px;
  border: none;
  background: ${({ $active }) => $active ? theme.colors.bgDark : 'transparent'};
  color: ${({ $active }) => $active ? '#FFFFFF' : theme.colors.textPrimary};
  font-family: ${theme.fonts.sans};
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all ${theme.transitions.fast};
  white-space: nowrap;

  &:not(:last-child) {
    border-right: 1px solid ${theme.colors.border};
  }

  &:hover {
    background: ${({ $active }) => $active ? theme.colors.bgDark : theme.colors.bgSection};
  }

  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: 7px 12px;
    font-size: 12px;
  }
`;

export const FilterPriceGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;

  @media (max-width: ${theme.breakpoints.mobile}) {
    flex: 1 1 100%;
  }
`;

export const FilterPriceInput = styled.input`
  width: 100px;
  padding: 8px 10px;
  border: 1px solid ${theme.colors.border};
  border-radius: 6px;
  font-family: ${theme.fonts.sans};
  font-size: 13px;
  color: ${theme.colors.textPrimary};
  transition: border-color ${theme.transitions.fast};

  &::placeholder {
    color: ${theme.colors.textMuted};
  }

  &:hover, &:focus {
    border-color: ${theme.colors.textPrimary};
    outline: none;
  }

  @media (max-width: ${theme.breakpoints.mobile}) {
    width: auto;
    flex: 1;
    font-size: 12px;
    padding: 7px 8px;
  }
`;

export const FilterPriceSep = styled.span`
  font-size: 13px;
  color: ${theme.colors.textMuted};
`;

export const FilterClearBtn = styled.button`
  padding: 8px 12px;
  background: none;
  border: 1px solid ${theme.colors.border};
  border-radius: 6px;
  font-family: ${theme.fonts.sans};
  font-size: 13px;
  font-weight: 500;
  color: ${theme.colors.textSecondary};
  cursor: pointer;
  transition: all ${theme.transitions.fast};
  white-space: nowrap;

  &:hover {
    border-color: ${theme.colors.textPrimary};
    color: ${theme.colors.textPrimary};
  }

  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: 7px 10px;
    font-size: 12px;
  }
`;

export const FilterSearchInput = styled.input`
  width: 220px;
  flex-shrink: 1;
  min-width: 120px;
  padding: 8px 10px 8px 32px;
  border: 1px solid ${theme.colors.border};
  border-radius: 6px;
  font-family: ${theme.fonts.sans};
  font-size: 13px;
  color: ${theme.colors.textPrimary};
  transition: border-color ${theme.transitions.fast};
  background: ${theme.colors.bgCard} url("data:image/svg+xml,%3Csvg width='14' height='14' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Ccircle cx='11' cy='11' r='7' stroke='%238A8F9A' stroke-width='2'/%3E%3Cpath d='M16 16l4.5 4.5' stroke='%238A8F9A' stroke-width='2' stroke-linecap='round'/%3E%3C/svg%3E") no-repeat 10px center;

  &::placeholder {
    color: ${theme.colors.textMuted};
  }

  &:hover, &:focus {
    border-color: ${theme.colors.textPrimary};
    outline: none;
  }

  @media (max-width: ${theme.breakpoints.mobile}) {
    flex: 1 1 100%;
    width: auto;
    font-size: 12px;
    padding: 7px 8px 7px 28px;
  }
`;

// ===== AMENITY TAGS =====

export const AmenityGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

export const AmenityTag = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  border: 1px solid ${theme.colors.border};
  border-radius: 20px;
  font-size: 13px;
  color: ${theme.colors.textPrimary};
`;

// ===== FOOTER =====

export const FooterWrapper = styled.footer`
  background: ${theme.colors.bgDark};
  color: rgba(255,255,255,0.7);
  padding: ${theme.spacing.xxl} 0 ${theme.spacing.lg};
`;

export const FooterGrid = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr;
  gap: ${theme.spacing.xl};
  margin-bottom: ${theme.spacing.xl};

  @media (max-width: ${theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
    gap: ${theme.spacing.lg};
  }
`;

export const FooterTitle = styled.h4`
  font-size: 13px;
  font-weight: 500;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: ${theme.colors.accentWarm};
  margin: 0 0 ${theme.spacing.sm} 0;
`;

export const FooterLink = styled.a`
  display: block;
  font-size: 14px;
  color: rgba(255,255,255,0.6);
  text-decoration: none;
  margin-bottom: 10px;
  transition: color ${theme.transitions.fast};

  &:hover {
    color: #FFFFFF;
  }
`;

export const FooterBottom = styled.div`
  border-top: 1px solid rgba(255,255,255,0.1);
  padding-top: ${theme.spacing.md};
  font-size: 13px;
  color: rgba(255,255,255,0.4);
`;

// ===== BREADCRUMBS =====

export const Breadcrumbs = styled.nav`
  font-size: 13px;
  color: ${theme.colors.textMuted};
  padding: ${theme.spacing.sm} 0;

  a {
    color: ${theme.colors.textSecondary};
    text-decoration: none;
    &:hover { color: ${theme.colors.textPrimary}; }
  }

  span { margin: 0 6px; }
`;

// ===== LISTING SPLIT LAYOUT =====

export const ListingSplit = styled.div`
  display: flex;
  width: 100%;
  max-width: 1600px;
  margin: 0 auto;

  @media (max-width: 1023px) {
    display: block;
  }
`;

export const ListingLeft = styled.div`
  flex: 1 1 60%;
  min-width: 0;
  padding: ${theme.spacing.lg} ${theme.spacing.md} ${theme.spacing.xl};

  @media (max-width: 599px) {
    padding: ${theme.spacing.md} ${theme.spacing.sm} ${theme.spacing.xl};
  }
`;

export const ListingRight = styled.aside`
  flex: 0 0 40%;
  position: sticky;
  top: 132px;
  align-self: flex-start;
  height: calc(100vh - 132px);
  border-left: 1px solid ${theme.colors.border};

  @media (max-width: 1023px) {
    display: none;
  }
`;

export const MapMobileFab = styled.button`
  position: fixed;
  left: 50%;
  bottom: 24px;
  transform: translateX(-50%);
  z-index: 950;
  background: ${theme.colors.bgDark};
  color: #FFFFFF;
  border: none;
  border-radius: 999px;
  padding: 12px 22px;
  font-family: ${theme.fonts.sans};
  font-size: 13px;
  font-weight: 500;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  cursor: pointer;
  display: none;
  align-items: center;
  gap: 8px;
  box-shadow: 0 4px 16px rgba(0,0,0,0.2);
  transition: transform ${theme.transitions.fast};

  &:active {
    transform: translateX(-50%) scale(0.96);
  }

  @media (max-width: 1023px) {
    display: inline-flex;
  }
`;

export const MapMobileOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 2000;
  background: ${theme.colors.bgCard};
  display: flex;
  flex-direction: column;
`;

export const MapMobileHeader = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: calc(14px + env(safe-area-inset-top)) 16px 14px;
  border-bottom: 1px solid ${theme.colors.border};
  background: ${theme.colors.bgCard};
  position: relative;
  z-index: 2001;

  h2 {
    margin: 0;
    font-family: ${theme.fonts.sans};
    font-size: 15px;
    font-weight: 500;
    color: ${theme.colors.textPrimary};
  }

  button {
    background: ${theme.colors.bgDark};
    border: none;
    border-radius: 999px;
    padding: 10px 20px;
    font-family: ${theme.fonts.sans};
    font-size: 13px;
    font-weight: 500;
    letter-spacing: 0.04em;
    color: #FFFFFF;
    cursor: pointer;
    transition: transform ${theme.transitions.fast};
    -webkit-tap-highlight-color: transparent;
    touch-action: manipulation;

    &:active { transform: scale(0.96); }
  }
`;

export const MapMobileBody = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  position: relative;
`;

// ===== TESTIMONIALS =====

export const TestimonialsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: ${theme.spacing.lg};
  margin-top: ${theme.spacing.xl};

  @media (max-width: ${theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
    gap: ${theme.spacing.md};
    max-width: 560px;
    margin-left: auto;
    margin-right: auto;
  }
`;

export const TestimonialCard = styled.figure`
  margin: 0;
  padding: ${theme.spacing.lg};
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 2px;
  display: flex;
  flex-direction: column;
  text-align: left;
  transition: border-color ${theme.transitions.base}, background ${theme.transitions.base};

  &:hover {
    border-color: rgba(148, 134, 113, 0.35);
    background: rgba(255, 255, 255, 0.05);
  }
`;

export const TestimonialMark = styled.div`
  font-family: ${theme.fonts.serif};
  font-size: 56px;
  line-height: 0.6;
  color: ${theme.colors.accentWarm};
  margin-bottom: ${theme.spacing.sm};
  user-select: none;
`;

export const TestimonialQuote = styled.blockquote`
  margin: 0 0 ${theme.spacing.md};
  padding: 0;
  font-family: ${theme.fonts.serif};
  font-style: italic;
  font-size: 17px;
  line-height: 1.6;
  color: rgba(255, 255, 255, 0.88);
  flex: 1;
`;

export const TestimonialAuthor = styled.figcaption`
  padding-top: ${theme.spacing.sm};
  border-top: 1px solid rgba(255, 255, 255, 0.1);

  strong {
    display: block;
    font-family: ${theme.fonts.sans};
    font-weight: 500;
    font-size: 14px;
    letter-spacing: 0.02em;
    color: #FFFFFF;
  }

  span {
    display: block;
    margin-top: 2px;
    font-family: ${theme.fonts.sans};
    font-size: 12px;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: ${theme.colors.accentWarm};
  }
`;

// ===== VISITA — AGENDAMENTO ESTILO QUINTO ANDAR =====

export const VisitLayout = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 360px;
  gap: ${theme.spacing.xl};
  align-items: start;

  @media (max-width: ${theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
  }
`;

export const VisitMain = styled.div`
  min-width: 0;
  background: ${theme.colors.bgCard};
  border: 1px solid ${theme.colors.border};
  border-radius: 12px;
  padding: ${theme.spacing.xl};

  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: ${theme.spacing.md};
  }
`;

export const VisitStep = styled.div`
  display: flex;
  flex-direction: column;
`;

export const VisitAsideCard = styled.aside`
  position: sticky;
  top: 100px;
  width: 100%;
  background: ${theme.colors.bgCard};
  border: 1px solid ${theme.colors.border};
  border-radius: 12px;
  overflow: hidden;

  @media (max-width: ${theme.breakpoints.tablet}) {
    position: static;
  }
`;

export const VisitAsideMobileHidden = styled.div`
  @media (max-width: ${theme.breakpoints.tablet}) {
    display: none;
  }
`;

export const VisitAsideImg = styled.img`
  width: 100%;
  height: 200px;
  object-fit: cover;
  display: block;
`;

export const VisitAsideInfo = styled.div`
  padding: ${theme.spacing.md};
  font-family: ${theme.fonts.sans};

  .title {
    font-family: ${theme.fonts.serif};
    font-size: 20px;
    color: ${theme.colors.textPrimary};
    line-height: 1.2;
    margin-bottom: 8px;
  }
  .price {
    font-size: 22px;
    font-weight: 700;
    color: ${theme.colors.textPrimary};
    margin-bottom: 8px;
  }
  .specs {
    font-size: 13px;
    color: ${theme.colors.textSecondary};
    margin-bottom: 10px;
  }
  .address {
    font-size: 13px;
    color: ${theme.colors.textMuted};
    line-height: 1.4;
  }
`;

export const DatePickerRow = styled.div`
  display: flex;
  gap: 10px;
  overflow-x: auto;
  padding: ${theme.spacing.sm} 0;
  scrollbar-width: thin;

  &::-webkit-scrollbar { height: 4px; }
  &::-webkit-scrollbar-thumb { background: ${theme.colors.border}; border-radius: 4px; }
`;

export const DateChip = styled.button`
  flex: 0 0 auto;
  width: 64px;
  height: 72px;
  border-radius: 12px;
  border: 1px solid ${({ $selected }) => ($selected ? theme.colors.bgDark : theme.colors.border)};
  background: ${({ $selected }) => ($selected ? theme.colors.bgDark : '#FFFFFF')};
  color: ${({ $selected }) => ($selected ? '#FFFFFF' : theme.colors.textPrimary)};
  font-family: ${theme.fonts.sans};
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  transition: all ${theme.transitions.fast};

  .dow {
    font-size: 11px;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    opacity: ${({ $selected }) => ($selected ? 0.85 : 0.6)};
  }
  .day {
    font-size: 20px;
    font-weight: 600;
  }

  &:hover:not(:disabled) {
    border-color: ${theme.colors.textMuted};
  }
`;

export const TimeSlotGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 8px;
  margin-top: ${theme.spacing.sm};

  @media (max-width: ${theme.breakpoints.mobile}) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

export const TimeSlot = styled.button`
  padding: 12px 8px;
  border-radius: 10px;
  border: 1px solid ${({ $selected }) => ($selected ? theme.colors.bgDark : theme.colors.border)};
  background: ${({ $selected }) => ($selected ? theme.colors.bgDark : '#FFFFFF')};
  color: ${({ $selected }) => ($selected ? '#FFFFFF' : theme.colors.textPrimary)};
  font-family: ${theme.fonts.sans};
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all ${theme.transitions.fast};

  &:hover:not(:disabled) {
    border-color: ${theme.colors.textMuted};
  }
`;

export const VisitSummary = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.sm};
  margin-top: ${theme.spacing.md};
  padding: ${theme.spacing.md};
  background: ${theme.colors.bgSection};
  border-radius: 10px;
`;

export const VisitSummaryItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  font-family: ${theme.fonts.sans};

  svg {
    flex-shrink: 0;
    width: 20px;
    height: 20px;
    color: ${theme.colors.textSecondary};
    margin-top: 2px;
  }

  .label {
    font-size: 12px;
    color: ${theme.colors.textSecondary};
    letter-spacing: 0.04em;
    text-transform: uppercase;
    margin-bottom: 2px;
  }
  .value {
    font-size: 15px;
    font-weight: 500;
    color: ${theme.colors.textPrimary};
  }
`;

export const VisitInfoBoxes = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${theme.spacing.sm};
  margin-top: ${theme.spacing.md};

  @media (max-width: ${theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
  }
`;

export const VisitInfoBox = styled.div`
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  background: ${theme.colors.bgSection};
  border-radius: 10px;
  font-family: ${theme.fonts.sans};
  font-size: 13px;
  color: ${theme.colors.textSecondary};
  line-height: 1.5;
`;

export const VisitForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.sm};
  margin-top: ${theme.spacing.lg};
`;

export const VisitField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-family: ${theme.fonts.sans};

  label {
    font-size: 13px;
    font-weight: 500;
    color: ${theme.colors.textSecondary};
  }

  input, textarea {
    padding: 12px 14px;
    border: 1px solid ${({ $invalid }) => ($invalid ? '#C4463A' : theme.colors.border)};
    border-radius: 10px;
    font-family: ${theme.fonts.sans};
    font-size: 15px;
    color: ${theme.colors.textPrimary};
    background: ${theme.colors.bgCard};
    transition: border-color ${theme.transitions.fast};
    resize: vertical;

    &:focus {
      outline: none;
      border-color: ${theme.colors.textPrimary};
    }
  }

  .err {
    font-size: 12px;
    color: #C4463A;
    margin-top: 2px;
  }
`;

export const VisitFooter = styled.div`
  display: flex;
  gap: ${theme.spacing.sm};
  margin-top: ${theme.spacing.lg};

  & > * {
    flex: 1;
  }
`;

export const VisitSuccess = styled.div`
  text-align: center;
  padding: ${theme.spacing.xl} ${theme.spacing.md};

  svg {
    width: 56px;
    height: 56px;
    color: #22845D;
    margin: 0 auto;
    stroke-width: 1.5;
  }

  a {
    color: ${theme.colors.textPrimary};
    text-decoration: underline;
  }
`;

// ===== DETAIL — SEÇÕES INSPIRADAS NO QUINTO ANDAR =====

// Card endereço: clicável, scrolla até o mapa embaixo. Mapa real no background.
export const AddressCard = styled.button`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${theme.spacing.md};
  width: 100%;
  margin-top: ${theme.spacing.xl};
  padding: ${theme.spacing.md} ${theme.spacing.md};
  background: ${theme.colors.bgSection};
  border: 1px solid ${theme.colors.border};
  border-radius: 12px;
  overflow: hidden;
  cursor: pointer;
  text-align: left;
  font-family: ${theme.fonts.sans};
  transition: border-color ${theme.transitions.fast}, transform ${theme.transitions.fast};

  &:hover {
    border-color: ${theme.colors.textMuted};
  }
  &:active { transform: scale(0.998); }
`;

// Fundo do AddressCard com mini-mapa real (Leaflet), blur sutil pro texto respirar
export const AddressMapBg = styled.div`
  position: absolute;
  inset: 0;
  z-index: 0;
  pointer-events: none;
  opacity: 0.65;
  filter: blur(2.5px) saturate(0.9);

  /* gradiente pra texto contrastar com o mapa */
  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(
      90deg,
      rgba(244, 244, 242, 0.88) 0%,
      rgba(244, 244, 242, 0.55) 45%,
      rgba(244, 244, 242, 0.25) 100%
    );
  }

  .leaflet-container {
    width: 100%;
    height: 100%;
    background: ${theme.colors.bgSection};
  }
  .leaflet-control-container { display: none; }
  .leaflet-marker-pane,
  .leaflet-shadow-pane { display: none; }
`;

// Conteúdo do card (acima do mapa)
export const AddressCardContent = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${theme.spacing.md};
  width: 100%;

  .address-main {
    font-size: 16px;
    font-weight: 600;
    color: ${theme.colors.textPrimary};
    line-height: 1.2;
  }

  .address-sub {
    margin-top: 4px;
    font-size: 13px;
    color: ${theme.colors.textSecondary};
  }

  .address-arrow {
    flex-shrink: 0;
    width: 36px;
    height: 36px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    background: ${theme.colors.bgCard};
    border: 1px solid ${theme.colors.border};
    color: ${theme.colors.textPrimary};
    font-size: 18px;
  }
`;

// Specs em grid 4x2 (desktop) / 2x4 (mobile)
export const SpecsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: ${theme.spacing.md} ${theme.spacing.lg};
  margin-top: ${theme.spacing.xl};

  @media (max-width: ${theme.breakpoints.tablet}) {
    grid-template-columns: repeat(2, 1fr);
    gap: ${theme.spacing.md};
  }
`;

export const SpecCell = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  font-family: ${theme.fonts.sans};
  font-size: 15px;
  color: ${theme.colors.textPrimary};

  svg {
    flex-shrink: 0;
    width: 22px;
    height: 22px;
    color: ${theme.colors.textSecondary};
    stroke-width: 1.5;
  }

  .spec-sub {
    display: block;
    font-size: 12px;
    color: ${theme.colors.textMuted};
    font-weight: 400;
  }
`;

// Chip de código do imóvel + meta inline de "Publicado há X"
export const CodeMetaRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.md};
  margin-top: ${theme.spacing.lg};
  flex-wrap: wrap;
`;

export const CodeChip = styled.span`
  display: inline-block;
  padding: 4px 12px;
  background: ${theme.colors.bgSection};
  border-radius: 6px;
  font-family: ${theme.fonts.sans};
  font-size: 12px;
  font-weight: 500;
  color: ${theme.colors.textSecondary};
  letter-spacing: 0.02em;
`;

export const MetaInline = styled.span`
  font-family: ${theme.fonts.sans};
  font-size: 13px;
  color: ${theme.colors.textMuted};
`;

// Descrição com clamp + botão "Ver mais"
export const DescriptionBlock = styled.div`
  margin-top: ${theme.spacing.xl};

  .desc-text {
    font-family: ${theme.fonts.sans};
    font-size: 15px;
    line-height: 1.6;
    color: ${theme.colors.textPrimary};
    white-space: pre-wrap;
    overflow-wrap: break-word;
    word-break: break-word;
    overflow: hidden;

    ${({ $expanded }) =>
      !$expanded &&
      `
      display: -webkit-box;
      -webkit-line-clamp: 5;
      -webkit-box-orient: vertical;
    `}
  }

  .desc-rich {
    white-space: normal;
    p { margin: 0 0 10px; }
    h1, h2, h3 {
      font-family: ${theme.fonts.serif || theme.fonts.sans};
      font-weight: 600;
      margin: 16px 0 8px;
      color: ${theme.colors.textPrimary};
    }
    h2 { font-size: 20px; }
    h3 { font-size: 17px; }
    ul, ol { margin: 6px 0 12px; padding-left: 22px; }
    li { margin: 3px 0; }
    strong { font-weight: 700; }
    em { font-style: italic; }
    blockquote {
      border-left: 3px solid ${theme.colors.accentWarm || '#B8935A'};
      padding-left: 14px;
      margin: 10px 0;
      font-style: italic;
      color: ${theme.colors.textSecondary || theme.colors.textPrimary};
    }
    a {
      color: ${theme.colors.accentWarm || '#B8935A'};
      text-decoration: underline;
      text-underline-offset: 3px;
    }
  }

  .desc-toggle {
    margin-top: 12px;
    background: none;
    border: none;
    padding: 0;
    font-family: ${theme.fonts.sans};
    font-size: 14px;
    font-weight: 600;
    color: ${theme.colors.textPrimary};
    cursor: pointer;
    text-decoration: underline;
    text-underline-offset: 4px;

    &:hover { color: ${theme.colors.accentWarm}; }
  }
`;

// Lista de amenidades em 2 colunas com check verde
export const AmenityList = styled.ul`
  list-style: none;
  padding: 0;
  margin: ${theme.spacing.md} 0 0;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px ${theme.spacing.lg};

  @media (max-width: ${theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
    gap: 10px;
  }
`;

export const AmenityItem = styled.li`
  display: flex;
  align-items: center;
  gap: 10px;
  font-family: ${theme.fonts.sans};
  font-size: 14px;
  color: ${theme.colors.textPrimary};

  svg {
    flex-shrink: 0;
    width: 18px;
    height: 18px;
    color: #22845D; /* verde check discreto */
    stroke-width: 2;
  }
`;

// Linha de ações inline (Favoritar + Compartilhar) estilo Quinto
export const SideActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.lg};
  padding-top: ${theme.spacing.sm};
  border-top: 1px solid ${theme.colors.border};
  margin-top: ${theme.spacing.sm};
`;

export const SideActionButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  background: none;
  border: none;
  padding: 4px 0;
  font-family: ${theme.fonts.sans};
  font-size: 14px;
  font-weight: 500;
  color: ${({ $active }) => ($active ? theme.colors.accentWarm : theme.colors.textPrimary)};
  cursor: pointer;
  transition: color ${theme.transitions.fast};

  svg {
    width: 18px;
    height: 18px;
    ${({ $active }) => $active && `fill: currentColor;`}
    stroke-width: 2;
  }

  &:hover { color: ${theme.colors.accentWarm}; }
`;

// Tabela de valores na sidebar (Aluguel/Venda, Condomínio, IPTU, Total)
export const ValuesTable = styled.div`
  margin-top: ${theme.spacing.md};
  border-top: 1px solid ${theme.colors.border};
  padding-top: ${theme.spacing.md};
`;

export const ValuesRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  padding: 8px 0;
  font-family: ${theme.fonts.sans};

  .val-label {
    font-size: 14px;
    color: ${theme.colors.textSecondary};
  }

  .val-amount {
    font-size: 14px;
    color: ${theme.colors.textPrimary};
    font-weight: 500;
  }

  &.total {
    margin-top: 6px;
    padding-top: ${theme.spacing.sm};
    border-top: 1px solid ${theme.colors.border};

    .val-label { font-size: 15px; color: ${theme.colors.textPrimary}; font-weight: 600; }
    .val-amount { font-size: 18px; font-weight: 700; }
  }
`;

// ============ Floor Plans Accordion ============
export const FloorsAccordion = styled.div`
  background: ${theme.colors.bgCard || '#FFFFFF'};
  border: 1px solid ${theme.colors.border};
  border-radius: 12px;
  overflow: hidden;
  margin-top: ${theme.spacing.md};
`;

export const Floor = styled.div`
  border-bottom: 1px solid ${theme.colors.border};
  &:last-child { border-bottom: none; }
`;

export const FloorHead = styled.button`
  width: 100%;
  display: grid;
  grid-template-columns: 1fr auto auto auto auto 24px;
  gap: 20px;
  align-items: center;
  padding: 16px 22px;
  cursor: pointer;
  background: ${({ $open }) => $open ? (theme.colors.bgAlt || '#F5F3EE') : 'transparent'};
  border: none;
  border-bottom: ${({ $open }) => $open ? `1px solid ${theme.colors.border}` : 'none'};
  text-align: left;
  font-family: ${theme.fonts.sans};
  transition: background .15s;
  &:hover { background: ${theme.colors.bgAlt || '#F5F3EE'}; }

  @media (max-width: ${theme.breakpoints.tablet}) {
    grid-template-columns: 1fr 24px;
    gap: 8px;
    & > *:nth-child(2),
    & > *:nth-child(3),
    & > *:nth-child(4),
    & > *:nth-child(5) {
      grid-column: 1;
      font-size: 11px;
    }
  }

  .floor-name {
    font-family: ${theme.fonts.serif || theme.fonts.sans};
    font-size: 19px;
    font-weight: 600;
    color: ${theme.colors.textPrimary};
  }
  .floor-stat {
    font-size: 12px;
    color: ${theme.colors.textPrimary};
    white-space: nowrap;
  }
  .floor-stat .k {
    color: ${theme.colors.textSecondary};
    margin-right: 4px;
    letter-spacing: 0.3px;
  }
  .floor-stat .v {
    font-weight: 600;
  }
  .floor-stat.price .v {
    font-family: ${theme.fonts.serif || theme.fonts.sans};
    font-size: 15px;
    color: ${theme.colors.accentWarm};
  }
  .chev {
    color: ${({ $open }) => $open ? theme.colors.accentWarm : theme.colors.textSecondary};
    transition: transform .2s;
    transform: ${({ $open }) => $open ? 'rotate(180deg)' : 'rotate(0)'};
    font-size: 14px;
    line-height: 1;
  }
`;

export const FloorBody = styled.div`
  padding: 22px;
  display: ${({ $open }) => $open ? 'block' : 'none'};
`;

export const FloorPlanImg = styled.div`
  background: ${theme.colors.bgAlt || '#F5F3EE'};
  border: 1px solid ${theme.colors.border};
  border-radius: 8px;
  overflow: hidden;
  aspect-ratio: 16/10;
  background-image: ${({ $src }) => $src ? `url("${$src}")` : 'none'};
  background-size: contain;
  background-position: center;
  background-repeat: no-repeat;
  cursor: ${({ $src }) => $src ? 'zoom-in' : 'default'};
  position: relative;

  ${({ $empty }) => $empty && `
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${theme.colors.textSecondary};
    font-size: 11px;
    letter-spacing: 1.5px;
    text-transform: uppercase;
  `}

  .zoom-icon {
    position: absolute;
    top: 12px; right: 12px;
    width: 32px; height: 32px;
    border-radius: 50%;
    background: rgba(255,255,255,.9);
    color: ${theme.colors.textPrimary};
    display: flex; align-items: center; justify-content: center;
    font-size: 14px;
    box-shadow: 0 2px 6px rgba(0,0,0,.15);
    opacity: 0;
    transition: opacity .15s;
  }
  &:hover .zoom-icon { opacity: 1; }
`;

export const FloorDesc = styled.div`
  font-size: 14px;
  color: ${theme.colors.textPrimary};
  line-height: 1.6;
  margin-top: 16px;
  padding-top: 14px;
  border-top: 1px dashed ${theme.colors.border};
`;

// ============ Lightbox simples para planta baixa ============
export const FloorPlanLightbox = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,.9);
  z-index: 999;
  display: ${({ $open }) => $open ? 'flex' : 'none'};
  align-items: center;
  justify-content: center;
  padding: 40px;

  img {
    max-width: 100%;
    max-height: 100%;
    border-radius: 6px;
    background: ${theme.colors.bgCard};
  }

  button.close {
    position: absolute;
    top: 20px;
    right: 20px;
    width: 42px;
    height: 42px;
    border-radius: 50%;
    background: rgba(255,255,255,.15);
    color: #fff;
    border: none;
    font-size: 22px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  button.close:hover { background: rgba(255,255,255,.25); }
`;

// ============ Calculadora de financiamento ============
export const MortgageBox = styled.div`
  background: ${theme.colors.bgCard || '#FFFFFF'};
  border: 1px solid ${theme.colors.border};
  border-radius: 12px;
  padding: 24px 26px;
  margin-top: ${theme.spacing.md};
`;

export const MortgageGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 14px;

  @media (max-width: ${theme.breakpoints.tablet}) {
    grid-template-columns: 1fr 1fr;
  }
`;

export const MortgageField = styled.label`
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-family: ${theme.fonts.sans};

  .label {
    font-size: 10px;
    letter-spacing: 1.5px;
    text-transform: uppercase;
    color: ${theme.colors.textSecondary};
    font-weight: 500;
  }

  input {
    border: 1px solid ${theme.colors.border};
    background: ${theme.colors.bg || '#FAFAF7'};
    color: ${theme.colors.textPrimary};
    padding: 10px 12px;
    border-radius: 7px;
    font-family: inherit;
    font-size: 14px;
    outline: none;
    transition: border-color .15s;

    &:focus {
      border-color: ${theme.colors.accentWarm};
      box-shadow: 0 0 0 3px ${theme.colors.accentWarmSoft || 'rgba(184,147,90,.18)'};
    }
  }
`;

export const MortgageResult = styled.div`
  margin-top: 20px;
  padding-top: 18px;
  border-top: 1px solid ${theme.colors.border};
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;

  .lbl {
    font-size: 11px;
    letter-spacing: 1.5px;
    color: ${theme.colors.textSecondary};
    text-transform: uppercase;
    font-weight: 500;
  }
  .val {
    font-family: ${theme.fonts.serif || theme.fonts.sans};
    font-size: 30px;
    font-weight: 600;
    color: ${theme.colors.accentWarm};
    line-height: 1;
  }
  .hint {
    font-size: 11px;
    color: ${theme.colors.textSecondary};
    width: 100%;
    margin-top: 4px;
  }
`;

// ============ Header limpo (título + badges, sem preço) ============
export const ListingHeader = styled.header`
  padding-bottom: 18px;
  border-bottom: 1px solid ${theme.colors.border};
  margin-bottom: 28px;
`;
export const ListingPre = styled.div`
  font-size: 11px;
  letter-spacing: 2.5px;
  color: ${theme.colors.accentWarm};
  text-transform: uppercase;
  font-weight: 500;
  margin-bottom: 6px;
`;
export const ListingTitle = styled.h1`
  font-family: ${theme.fonts.serif || theme.fonts.sans};
  font-size: 38px;
  font-weight: 500;
  letter-spacing: -0.3px;
  color: ${theme.colors.textPrimary};
  line-height: 1.1;
  margin: 0 0 6px;
  @media (max-width: ${theme.breakpoints.tablet}) { font-size: 28px; }
`;
export const ListingAddress = styled.div`
  color: ${theme.colors.textSecondary};
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 6px;
  svg { color: ${theme.colors.accentWarm}; }
`;
export const ListingBadges = styled.div`
  display: flex;
  gap: 6px;
  margin-top: 12px;
  flex-wrap: wrap;
`;
export const ListingBadge = styled.span`
  font-size: 10px;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  padding: 4px 10px;
  border-radius: 999px;
  background: ${({ $gold }) => $gold ? theme.colors.accentWarm : (theme.colors.bgAlt || '#F2EBDD')};
  color: ${({ $gold }) => $gold ? '#fff' : theme.colors.textPrimary};
  font-weight: 600;
  border: 1px solid ${({ $gold }) => $gold ? theme.colors.accentWarm : 'transparent'};
`;

// ============ Section heading com filete dourado ============
export const SectionTitle = styled.h2`
  font-family: ${theme.fonts.serif || theme.fonts.sans};
  font-size: 26px;
  font-weight: 600;
  color: ${theme.colors.textPrimary};
  margin: 0 0 4px;
  &::after {
    content: '';
    display: block;
    width: 30px;
    height: 2px;
    background: ${theme.colors.accentWarm};
    margin-top: 6px;
    margin-bottom: 18px;
  }
`;

// ============ Overview (grid de stats c/ ícone redondo dourado) ============
export const OverviewGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 14px;
  background: ${theme.colors.bgCard || '#FFFFFF'};
  border: 1px solid ${theme.colors.border};
  border-radius: 12px;
  padding: 22px 24px;

  @media (max-width: ${theme.breakpoints.tablet}) { grid-template-columns: repeat(2, 1fr); }
`;
export const OverviewCell = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  .ic {
    width: 38px; height: 38px;
    border-radius: 50%;
    background: ${theme.colors.bgAlt || '#F2EBDD'};
    color: ${theme.colors.accentWarm};
    display: flex; align-items: center; justify-content: center;
    flex-shrink: 0;
    svg { width: 18px; height: 18px; }
  }
  .lbl {
    font-size: 10px;
    letter-spacing: 1.5px;
    color: ${theme.colors.textSecondary};
    text-transform: uppercase;
  }
  .val {
    font-family: ${theme.fonts.serif || theme.fonts.sans};
    font-size: 18px;
    font-weight: 600;
    color: ${theme.colors.textPrimary};
    line-height: 1.1;
    margin-top: 2px;
  }
`;

// ============ Endereço card ============
export const AddressV2 = styled.div`
  background: ${theme.colors.bgCard || '#FFFFFF'};
  border: 1px solid ${theme.colors.border};
  border-radius: 12px;
  padding: 20px 24px;
`;
export const AddressV2Head = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 14px;
  flex-wrap: wrap;
  gap: 10px;
  .hint { font-size: 13px; color: ${theme.colors.textSecondary}; }
`;
export const AddressV2MapBtn = styled.a`
  background: ${theme.colors.textPrimary};
  color: ${theme.colors.bg || '#FAFAF7'};
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 12px;
  letter-spacing: 0.5px;
  font-weight: 500;
  cursor: pointer;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;
export const AddressV2Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0 28px;
  font-size: 13px;
  @media (max-width: ${theme.breakpoints.tablet}) { grid-template-columns: 1fr; }

  .row {
    display: flex;
    gap: 10px;
    padding: 8px 0;
    border-bottom: 1px dashed ${theme.colors.border};
  }
  .row .k { color: ${theme.colors.textSecondary}; min-width: 100px; }
  .row .v { color: ${theme.colors.textPrimary}; font-weight: 500; }
`;

// ============ Detalhes ficha técnica ============
export const DetailsBox = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0 36px;
  background: ${theme.colors.bgCard || '#FFFFFF'};
  border: 1px solid ${theme.colors.border};
  border-radius: 12px;
  padding: 22px 24px;
  @media (max-width: ${theme.breakpoints.tablet}) { grid-template-columns: 1fr; }
`;
export const DetailsRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 10px 0;
  border-bottom: 1px dashed ${theme.colors.border};
  font-size: 13px;
  .k { color: ${theme.colors.textSecondary}; }
  .v { color: ${theme.colors.textPrimary}; font-weight: 500; }
`;

// ============ Features 3-col com check redondo dourado ============
export const FeaturesGrid3 = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px 24px;
  background: ${theme.colors.bgCard || '#FFFFFF'};
  border: 1px solid ${theme.colors.border};
  border-radius: 12px;
  padding: 20px 24px;
  @media (max-width: ${theme.breakpoints.tablet}) { grid-template-columns: repeat(2, 1fr); }
  @media (max-width: ${theme.breakpoints.mobile}) { grid-template-columns: 1fr; }
`;
export const FeatureItem = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 6px 0;
  font-size: 14px;
  color: ${theme.colors.textPrimary};
  .check {
    width: 18px; height: 18px; border-radius: 50%;
    background: ${theme.colors.bgAlt || '#F2EBDD'};
    color: ${theme.colors.accentWarm};
    display: flex; align-items: center; justify-content: center;
    flex-shrink: 0;
    svg { width: 10px; height: 10px; }
  }
`;

// ============ Mortgage compacto pra sidebar ============
export const MortgageSide = styled.div`
  background: ${theme.colors.bgCard || '#FFFFFF'};
  border: 1px solid ${theme.colors.border};
  border-radius: 12px;
  padding: 20px 22px;
  margin-top: 16px;

  h3 {
    font-family: ${theme.fonts.serif || theme.fonts.sans};
    font-size: 17px;
    font-weight: 600;
    color: ${theme.colors.textPrimary};
    margin: 0 0 14px;
    padding-bottom: 10px;
    border-bottom: 1px solid ${theme.colors.border};
  }

  .row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 10px;
  }
  .row.full { grid-template-columns: 1fr; }

  label {
    display: flex;
    flex-direction: column;
    gap: 4px;
    font-family: ${theme.fonts.sans};
    margin-bottom: 10px;
    min-width: 0;

    .label {
      font-size: 10px;
      letter-spacing: 1.5px;
      color: ${theme.colors.textSecondary};
      text-transform: uppercase;
      font-weight: 500;
    }
    input {
      width: 100%;
      max-width: 100%;
      box-sizing: border-box;
      border: 1px solid ${theme.colors.border};
      background: ${theme.colors.bg || '#FAFAF7'};
      color: ${theme.colors.textPrimary};
      padding: 9px 11px;
      border-radius: 6px;
      font-family: inherit;
      font-size: 13px;
      outline: none;
      &:focus { border-color: ${theme.colors.accentWarm}; }
    }
  }

  .result {
    margin-top: 4px;
    padding-top: 14px;
    border-top: 1px solid ${theme.colors.border};
    text-align: center;
    .lbl {
      font-size: 10px;
      letter-spacing: 1.5px;
      color: ${theme.colors.textSecondary};
      text-transform: uppercase;
      font-weight: 500;
    }
    .val {
      font-family: ${theme.fonts.serif || theme.fonts.sans};
      font-size: 28px;
      font-weight: 600;
      color: ${theme.colors.accentWarm};
      line-height: 1.1;
      margin-top: 4px;
    }
    .breakdown {
      margin-top: 12px;
      padding-top: 10px;
      border-top: 1px dashed ${theme.colors.border};
      text-align: left;
      .line {
        display: flex;
        justify-content: space-between;
        font-size: 11px;
        padding: 4px 0;
        span { color: ${theme.colors.textSecondary}; }
        b { color: ${theme.colors.textPrimary}; font-weight: 600; }
      }
    }
    .hint {
      font-size: 10px;
      color: ${theme.colors.textSecondary};
      margin-top: 10px;
      line-height: 1.4;
    }
  }
`;
